/**
 * Dietary labels and allergen vocabulary for menu items.
 *
 * Both lists are FIXED vocabularies, not free text. That is what makes them
 * translatable platform-wide (ordering.* keys resolve through
 * DEFAULT_TRANSLATIONS / core_translations, so clubs do no translation work)
 * and machine-readable for later features like "only gluten-free" filters.
 * The allergen list is exactly the 14 substances of EU Regulation 1169/2011
 * Annex II, which venues must declare for non-prepacked food.
 *
 * Ingredients are the one free-text piece; their resolution mirrors
 * resolveOrderReceivedNotice in src/lib/orderNotice.ts and is kept here, out
 * of TagPage, for the same reason: the precedence rules are testable without
 * rendering the fan page.
 */

export const DIETARY_TAGS = ['vegetarian', 'vegan', 'gluten_free'] as const;
export type DietaryTag = (typeof DIETARY_TAGS)[number];

export const DIETARY_TAG_LABELS: Record<DietaryTag, string> = {
  vegetarian: 'Vegetarian',
  vegan: 'Vegan',
  gluten_free: 'Gluten-free',
};

// Base hues for the card badges. Never paint these raw: the fan card sits on
// the club's brand colour, so components must walk them through
// legibleOn(colour, palette.cardSolid) first (see fanTheme.ts).
export const DIETARY_TAG_COLORS: Record<DietaryTag, string> = {
  vegetarian: '#2f7d46',
  vegan: '#1e7a4f',
  gluten_free: '#9c6a1c',
};

export const EU_ALLERGENS = [
  'gluten',
  'crustaceans',
  'eggs',
  'fish',
  'peanuts',
  'soybeans',
  'milk',
  'nuts',
  'celery',
  'mustard',
  'sesame',
  'sulphites',
  'lupin',
  'molluscs',
] as const;
export type Allergen = (typeof EU_ALLERGENS)[number];

export const ALLERGEN_LABELS: Record<Allergen, string> = {
  gluten: 'Gluten',
  crustaceans: 'Crustaceans',
  eggs: 'Eggs',
  fish: 'Fish',
  peanuts: 'Peanuts',
  soybeans: 'Soy',
  milk: 'Milk',
  nuts: 'Nuts',
  celery: 'Celery',
  mustard: 'Mustard',
  sesame: 'Sesame',
  sulphites: 'Sulphites',
  lupin: 'Lupin',
  molluscs: 'Molluscs',
};

// The ordering.* prefix routes these into the "Ordering" category on the club
// Translations page (getTranslationKeysByCategory keys off the prefix).
export const dietaryTagKey = (tag: DietaryTag) => `ordering.dietary.${tag}`;
export const allergenKey = (allergen: Allergen) => `ordering.allergen.${allergen}`;

// Per-item free text, same key family as menu.item.name.* / menu.item.size.*.
export const menuItemIngredientsKey = (itemId: string) => `menu.item.ingredients.${itemId}`;

/** The stored array filtered down to the vocabulary, order preserved. */
export function knownDietaryTags(tags: string[] | null | undefined): DietaryTag[] {
  return (tags ?? []).filter((t): t is DietaryTag => (DIETARY_TAGS as readonly string[]).includes(t));
}

export function knownAllergens(allergens: string[] | null | undefined): Allergen[] {
  return (allergens ?? []).filter((a): a is Allergen => (EU_ALLERGENS as readonly string[]).includes(a));
}

/**
 * What the fan should read as the item's ingredients, or '' for "render
 * nothing". Same precedence rules as resolveOrderReceivedNotice: the raw
 * column gates everything (a leftover translation row can never resurrect
 * cleared text), `t(key) === key` is the sentinel for "untranslated in this
 * locale" (the key deliberately has no DEFAULT_TRANSLATIONS entry), and a
 * blank translation means "not translated", not "hide it".
 */
export function resolveItemIngredients(
  raw: string | null | undefined,
  itemId: string,
  t: (key: string) => string,
): string {
  const base = (raw ?? '').trim();
  if (!base) return '';

  const key = menuItemIngredientsKey(itemId);
  const translated = t(key);
  if (translated === key) return base;

  return translated.trim() || base;
}

/** Whether the card should offer the info sheet at all. */
export function hasItemInfo(item: {
  dietary_tags?: string[] | null;
  allergens?: string[] | null;
  ingredients?: string | null;
}): boolean {
  return (
    knownDietaryTags(item.dietary_tags).length > 0 ||
    knownAllergens(item.allergens).length > 0 ||
    (item.ingredients ?? '').trim() !== ''
  );
}
